<template>
    <RouterLink
        v-if="to"
        data-spec-class="link"
        :to="to"
        :class="classObject"
        :title="title"
        @click="emits('click')"
    >
        <span
            v-if="isTruncated"
            class="block truncate"
        >
            <slot />
        </span>
        <template v-else><slot /></template>
        <component
            :is="icon"
            v-if="icon"
            class="ml-1 h-5 w-5"
            data-spec-class="link__icon"
        />
    </RouterLink>
    <a
        v-else
        data-spec-class="link"
        :href="href ? href : 'javascript:;'"
        :class="classObject"
        :title="title"
        @click="emits('click')"
    >
        <span
            v-if="isTruncated"
            class="block truncate"
        >
            <slot />
        </span>
        <template v-else><slot /></template>
        <component
            :is="icon"
            v-if="icon"
            class="ml-1 h-5 w-5"
            data-spec-class="link__icon"
        />
    </a>
</template>

<script setup lang="ts">
import { defineProps, withDefaults, defineEmits, computed } from 'vue';
import { ArrowTopRightOnSquareIcon } from '@heroicons/vue/20/solid';
import { Theme, IconKey } from '@/types';
import { RouteLocationNormalized } from 'vue-router';

const ICON_REPOSITORY = {
    [IconKey.Extern]: ArrowTopRightOnSquareIcon,
};

interface Props {
    theme?: Theme;
    to?: RouteLocationNormalized;
    href?: string;
    iconKey?: IconKey;
    customThemeBase?: string;
    customThemeNormal?: string;
    isTruncated?: boolean;
    title?: string;
}

const props = withDefaults(defineProps<Props>(), {
    theme: Theme.Normal,
    to: undefined,
    href: undefined,
    iconKey: undefined,
    customThemeBase: undefined,
    customThemeNormal: undefined,
    isTruncated: false,
    title: undefined,
});

const emits = defineEmits(['click']);

const themeBase = computed(() => {
    const defaultThemeBase = 'flex items-center hover:underline';
    return typeof props.customThemeBase === 'string' ? props.customThemeBase : defaultThemeBase;
});

const themeNormal = computed(() => {
    const defaultThemeNormal = 'text-brand hover:text-brand--light cursor-pointer';
    return typeof props.customThemeNormal === 'string'
        ? props.customThemeNormal
        : defaultThemeNormal;
});

const classObject = computed(() => {
    const baseClasses: Record<string, boolean | string> = {
        'transition-ease': true,
        [themeBase.value]: true,
    };

    if (props.theme === Theme.Normal) {
        baseClasses[themeNormal.value] = true;
    } else if (props.theme === Theme.Tertiary) {
        baseClasses['text-tertiary hover:text-muted'] = true;
    }

    return baseClasses;
});

const icon = computed(() => (props.iconKey ? ICON_REPOSITORY[props.iconKey] : undefined));
</script>
